import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { TRIAL_QUESTION_COUNT } from '../utils/trialQuiz';
import { isCertEnabled } from '../config/certAvailability';
import { useDocumentMeta } from '../hooks/useDocumentMeta';

const CERTIFICATIONS = [
    {
        id: 'comptia',
        name: 'CompTIA Cloud+',
        code: 'CV0-004',
        blurb: 'Cloud architecture, deployment, operations, security, DevOps fundamentals and troubleshooting.',
    },
    {
        id: 'aws',
        name: 'AWS Solutions Architect Associate',
        code: 'SAA-C03',
        blurb: 'Secure, resilient, high-performing and cost-optimized architectures on AWS.',
    },
];

const FEATURES = [
    {
        icon: '🎯',
        title: 'Exam-style questions',
        body: 'Single and multiple-answer questions written to match the real exam, spread across every domain.',
    },
    {
        icon: '💡',
        title: 'Explanations that teach',
        body: 'Every answer comes with a breakdown of why it is right and why the other options are not.',
    },
    {
        icon: '⏱️',
        title: 'Custom and timed quizzes',
        body: 'Focus on weak domains, pick a difficulty, or run a full exam simulation against the clock.',
    },
    {
        icon: '📈',
        title: 'Progress tracking',
        body: 'Your dashboard shows score trends, accuracy by domain and the areas to focus on next.',
    },
];

const STEPS = [
    { title: 'Try the sample quiz', body: `${TRIAL_QUESTION_COUNT} questions from different domains. No account needed.` },
    { title: 'Create a free account', body: 'Unlock the full question bank and save your results.' },
    { title: 'Practice and improve', body: 'Build custom quizzes and watch your scores climb on the dashboard.' },
];

const SampleQuizCta: React.FC = () => (
    <Link to="/try" className="landing-cta">
        Take the free sample quiz
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6"/>
        </svg>
    </Link>
);

const LandingPage: React.FC = () => {
    const { isAuthenticated, loading } = useAuth();
    useDocumentMeta(
        'CloudPrepper | CompTIA Cloud+ CV0-004 & AWS SAA-C03 Practice Exams',
        'Free CompTIA Cloud+ (CV0-004) and AWS Solutions Architect practice questions with detailed explanations, custom quizzes and progress tracking. Try a sample quiz, no account needed.',
        { path: '/welcome' }
    );

    if (loading) return null;
    if (isAuthenticated) return <Navigate to="/" replace />;

    return (
        <div className="page-below-nav landing-rainbow overflow-x-hidden">
            <main className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16 space-y-20">
                <section className="text-center max-w-3xl mx-auto">
                    <span className="landing-badge">Cloud certification practice</span>
                    <h1 className="mt-5 text-4xl sm:text-5xl font-bold leading-tight quiz-text">
                        Pass your cloud certification with confidence
                    </h1>
                    <p className="mt-5 text-lg quiz-text-muted">
                        CloudPrepper is a practice-exam platform for CompTIA Cloud+ and AWS Solutions Architect
                        Associate. Answer realistic exam questions, learn from detailed explanations, and track
                        your readiness domain by domain.
                    </p>
                    <div className="mt-8 flex flex-col items-center gap-3">
                        <SampleQuizCta />
                        <p className="text-sm quiz-text-muted">
                            {TRIAL_QUESTION_COUNT} questions · about 3 minutes · no account needed
                        </p>
                        <p className="text-sm quiz-text-muted">
                            Already have an account?{' '}
                            <Link to="/login" className="auth-link">Sign in</Link>
                        </p>
                    </div>
                </section>

                <section aria-labelledby="certs-heading">
                    <h2 id="certs-heading" className="text-2xl font-semibold text-center quiz-text">
                        Certifications covered
                    </h2>
                    <div className="mt-6 grid gap-4 sm:grid-cols-2">
                        {CERTIFICATIONS.map((cert) => (
                            <div key={cert.id} className="quiz-results-card p-6 flex flex-col">
                                <div className="flex items-baseline justify-between gap-3">
                                    <h3 className="text-lg font-semibold quiz-text">{cert.name}</h3>
                                    <span className="font-jetbrains text-sm quiz-text-muted">{cert.code}</span>
                                </div>
                                <p className="mt-2 text-sm quiz-text-muted flex-1">{cert.blurb}</p>
                                {isCertEnabled(cert.id) ? (
                                    <Link to={`/try?cert=${cert.id}`} className="mt-4 auth-link text-sm self-start">
                                        Try the {cert.name} sample →
                                    </Link>
                                ) : (
                                    <p className="mt-4 text-sm quiz-text-muted self-start">
                                        Coming soon — we&apos;re rebuilding this question bank.
                                    </p>
                                )}
                            </div>
                        ))}
                    </div>
                </section>

                <section aria-labelledby="features-heading">
                    <h2 id="features-heading" className="text-2xl font-semibold text-center quiz-text">
                        Everything you need to prepare
                    </h2>
                    <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        {FEATURES.map((feature) => (
                            <div key={feature.title} className="quiz-results-card p-5">
                                <div className="landing-icon" aria-hidden="true">{feature.icon}</div>
                                <h3 className="mt-3 font-semibold quiz-text">{feature.title}</h3>
                                <p className="mt-1 text-sm quiz-text-muted">{feature.body}</p>
                            </div>
                        ))}
                    </div>
                </section>

                <section aria-labelledby="steps-heading">
                    <h2 id="steps-heading" className="text-2xl font-semibold text-center quiz-text">How it works</h2>
                    <ol className="mt-6 grid gap-4 sm:grid-cols-3">
                        {STEPS.map((step, i) => (
                            <li key={step.title} className="quiz-results-card p-5 flex gap-4">
                                <span className="landing-step-num">{i + 1}</span>
                                <div>
                                    <h3 className="font-semibold quiz-text">{step.title}</h3>
                                    <p className="mt-1 text-sm quiz-text-muted">{step.body}</p>
                                </div>
                            </li>
                        ))}
                    </ol>
                </section>

                <section className="quiz-results-card trial-signup-card p-8 sm:p-10 text-center">
                    <h2 className="text-2xl sm:text-3xl font-bold quiz-text">See if CloudPrepper is right for you</h2>
                    <p className="mt-3 quiz-text-muted max-w-xl mx-auto">
                        Take the sample quiz now and get your score with full explanations in a few minutes.
                    </p>
                    <div className="mt-6 flex justify-center">
                        <SampleQuizCta />
                    </div>
                </section>
            </main>
        </div>
    );
};

export default LandingPage;
